// src/BusinessMetrics.js
//
// Audit sections 51-53 asked for fourteen marketplace numbers, nine revenue
// numbers and nine event counts. Almost none of them needed anything built —
// the rows were already in the database and nobody had done the division. The
// arithmetic lives in public.owner_business_metrics so there is one definition
// of "cancellation rate" rather than one per page.
//
// Every tile shows its basis, because a rate without its denominator is a
// rumour: "8.7% cancelled" means nothing until you know that expired requests
// were left out of the bottom of the fraction.
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, Tooltip } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PaidIcon from '@mui/icons-material/Paid';
import TimelineIcon from '@mui/icons-material/Timeline';
import DepthCard from './DepthCard';
import { supabase } from './supabaseClient';
import { ink, brand, surface, type as typeScale } from './designTokens';

const SECTIONS = [
  { key: 'marketplace', title: 'Marketplace', icon: <StorefrontIcon sx={{ fontSize: 20 }} />,
    blurb: 'Supply, demand and how often the two meet.' },
  { key: 'revenue', title: 'Revenue', icon: <PaidIcon sx={{ fontSize: 20 }} />,
    blurb: 'What came in, what was kept, and what went back out.' },
  { key: 'events', title: 'Counts', icon: <TimelineIcon sx={{ fontSize: 20 }} />,
    blurb: 'Counted from rows rather than from a tracker, so they cannot drift.' },
];

// Cents, not dollars, everywhere the unit says so — booking_ledger is in cents
// while bookings.price is in dollars, and mixing the two has produced wrong
// figures on this project before.
function format(value, unit) {
  if (value === null) return '—';
  if (unit === 'cents') {
    return (value / 100).toLocaleString(undefined, {
      style: 'currency', currency: 'USD', maximumFractionDigits: 2,
    });
  }
  if (unit === 'percent') return `${value}%`;
  if (unit === 'hours') return `${value} h`;
  if (unit === 'days') return `${value} d`;
  return value.toLocaleString();
}

function MetricTile({ metric }) {
  const empty = metric.value === null;
  return (
    <Tooltip title={metric.basis} placement="top" arrow>
      <Box>
        <DepthCard>
          <Typography
            sx={{
              color: ink[500], fontSize: typeScale.micro, letterSpacing: '0.08em',
              textTransform: 'uppercase', mb: 1.5,
            }}
          >
            {metric.label}
          </Typography>
          <Typography sx={{ color: empty ? ink[500] : ink[900], fontSize: 28, fontWeight: 600, lineHeight: 1 }}>
            {format(metric.value, metric.unit)}
          </Typography>
          {/* The basis is in the tooltip for every tile, but an empty one needs
              to say why on its face: a dash with no explanation reads as a bug. */}
          {empty && (
            <Typography sx={{ color: ink[500], fontSize: typeScale.micro, mt: 1 }}>
              nothing to divide by yet
            </Typography>
          )}
        </DepthCard>
      </Box>
    </Tooltip>
  );
}

export default function BusinessMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [readAt, setReadAt] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // The function runs on the service role because the view reads past RLS,
      // so it checks the caller itself and needs the session token. A cookie
      // is not enough: this page sent none and got "Missing bearer token".
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('Your session has expired. Sign in again.');
      const res = await fetch('/api/business-metrics', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setMetrics(body.metrics || []);
      setReadAt(body.readAt);
    } catch (err) {
      setError(err.message);
      setMetrics([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (metrics === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress sx={{ color: brand.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 5, overflowY: 'auto', height: '100%', bgcolor: surface.sunken }}>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {SECTIONS.map((section) => {
        const rows = metrics.filter((m) => m.section === section.key);
        if (rows.length === 0) return null;
        return (
          <Box key={section.key} sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: brand.gold }}>
              {section.icon}
              <Typography sx={{ color: ink[900], fontSize: 18, fontWeight: 600 }}>
                {section.title}
              </Typography>
            </Box>
            <Typography sx={{ color: ink[500], fontSize: typeScale.small, mb: 2 }}>
              {section.blurb}
            </Typography>
            <Grid container spacing={3}>
              {rows.map((m) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={m.metric_key}>
                  <MetricTile metric={m} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {readAt && (
        <Typography sx={{ color: ink[500], fontSize: typeScale.micro }}>
          Read from the live database at {new Date(readAt).toLocaleString()}. Hover a tile for how it is worked out.
        </Typography>
      )}
    </Box>
  );
}
